import React, {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useReducer,
    useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { FormsService } from '../../../service/FormService';
import { useFormTabStore } from '../../../store/storeFormTabs';
import { useNotificationStore } from '../../../store/storeNotifications';
import {
    ActionTypes,
    formBuilderReducer,
    FormBuilderState,
    IActionType,
} from '../Reducer';

export enum ITypeNode {
  TITLE = 'title',
  SHORT_TEXT = 'shortText',
  IMAGE = 'image',
  INPUT = 'input',
  LIST = 'list',
  OUTPUT = 'output',
  SUBMIT = 'submit',
}

export interface IStyles {
  fontFamily?: string;
  fontSize?: number;
  isBold?: boolean;
  isItalic?: boolean;
  isUnderlined?: boolean;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  color?: string;
  buttonColor?: string;
  buttonBackgroundColor?: string;
  objectFit?: 'cover' | 'contain';
}

export interface INode {
  id: string;
  type: ITypeNode;
  data: {
    title?: string;
    subtitle?: string;
    placeholder?: string;
    text?: string;
    url?: string;
    removeOrRetain?: boolean;
    redirectPageId?: string;
    varDataInputId?: string;
    varDataOutputId?: string;
    fieldType?: 'data' | 'upload' | 'both' | 'standard' | 'editor';
    requiredField?: boolean;
    listItems?: {
      description: string;
    }[];
    allowedFiles?: string[];
    minRows?: number;
    question?: string;
    typeQuestion?: 'dropdown' | 'single' | 'multiple';
    styles: {
      title?: IStyles;
      subtitle?: IStyles;
      shortText?: IStyles;
      submit?: IStyles;
      image?: IStyles;
      question?: IStyles;
      backgroundColor: string;
      borderRadius: number;
      borderWeight: number;
      borderColor: string;
      verticalAlign: 'top' | 'center' | 'bottom';
      padding: number,
    };
  };
  layout: {
    i: string;
    x: number;
    y: number;
    w: number;
    h: number;
    minW?: number;
    minH?: number;
    moved?: boolean;
    static?: boolean;
  };
}

export interface INodeFlow {
  id: string;
  type: string;
  data: {
    label: string;
  };
}

export interface IFlow {
  id: string;
  name: string;
  newFormNodes: INodeFlow[];
}

export interface IPage {
  id: string;
  name: string;
  nodeIds: string[];
}

interface FormBuilderContextType {
  state: FormBuilderState;
  dispatch: React.Dispatch<IActionType>;
  loadingForm: boolean;
  selectNode: INode | null;
  setSelectNode: React.Dispatch<React.SetStateAction<INode | null>>;
  setSettingsFormMode: React.Dispatch<React.SetStateAction<boolean>>;
  settingsFormMode: boolean;
  currentPageId: string | null;
  setCurrentPageId: React.Dispatch<React.SetStateAction<string | null>>;
}

const FormBuilderContext = createContext<FormBuilderContextType | undefined>(
  undefined,
);

interface FormBuilderProviderProps {
  children: ReactNode;
}

const initialState: FormBuilderState = {
  form: {
    id: '',
    name: '',
    flows: [],
    status: 'draft',
    activeFlows: [],
    publishType: 'internal',
    newFormNodes: [],
    newFormNodesPublished: [],
    formStyle: {
      backgroundColor: '',
      backgroundImage: '',
      pageOrientation: 'SWAP_PAGE',
    },
    formSettings: {
      successMessage: '',
    },
    updatedAt: new Date().toISOString(),
  },
  varInputNodes: [],
  varOutputNodes: [],
};

export function FormBuilderProvider({ children }: FormBuilderProviderProps) {
  const [loadingForm, setLoadingForm] = useState(false);
  const setNotification = useNotificationStore(state => state.setNotification);
  const { addFormTab } = useFormTabStore();

  const [state, dispatch] = useReducer(formBuilderReducer, initialState);
  const [selectNode, setSelectNode] = useState<INode | null>(null);
  const [settingsFormMode, setSettingsFormMode] = useState(true);
  const [currentPageId, setCurrentPageId] = useState<string | null>(null);
  const { formId } = useParams();

  useEffect(() => {
    if (
      selectNode &&
      !state.form.newFormNodes.find(node => node.id === selectNode.id)
    ) {
      setSelectNode(null);
    }
  }, [state.form.newFormNodes, selectNode]);

  useEffect(() => {
    const getFlows = async () => {
      try {
        setLoadingForm(true);

        const response = await FormsService.getForm(formId!);
        const responseInputsOutputs = await FormsService.inputsOutputs({
          activeFlows: response.data.activeFlows,
        });

        dispatch({
          type: ActionTypes.UPDATE_FORM,
          payload: {
            form: {
              ...response.data,
            },
            varInputNodes: responseInputsOutputs.data.varInputNode,
            varOutputNodes: responseInputsOutputs.data.varOutputNode,
          },
        });

        if (response.data && response.data.name) {
          addFormTab({
            formId: response.data.id,
            formName: response.data.name,
          });
        }

        setLoadingForm(false);
      } catch (error) {
        setNotification({
          type: 'error',
          message: 'Error finding your form',
        });
      }
    };

    if (formId) {
      getFlows();
    }
  }, [formId, addFormTab]);

  useEffect(() => {
    if (
      (!state.form.pages || state.form.pages.length === 0) &&
      state.form.newFormNodes.length > 0
    ) {
      dispatch({ type: ActionTypes.INITIALIZE_PAGES });
    }
    else if (
      state.form.pages &&
      state.form.pages.length > 0 &&
      !currentPageId
    ) {
      setCurrentPageId(state.form.pages[0].id);
    }
  }, [state.form.pages, state.form.newFormNodes.length, currentPageId, dispatch]);

  return (
    <FormBuilderContext.Provider
      value={{
        state,
        dispatch,
        loadingForm,
        selectNode,
        setSelectNode,
        setSettingsFormMode,
        settingsFormMode,
        currentPageId,
        setCurrentPageId,
      }}
    >
      {children}
    </FormBuilderContext.Provider>
  );
}

export function useFormBuilder() {
  const context = useContext(FormBuilderContext);

  if (context === undefined) {
    throw new Error('useFormBuilder must be used within a FormBuilderProvider');
  }
  return context;
}
